import { Box, HStack, Heading, Text } from "@chakra-ui/react"
import ScrollView from "@pathwright/ui/src/components/scroll/ScrollView"
import Link from "@pathwright/ux/src/components/link/Link"
import DotNav from "@pathwright/ux/src/components/nav/DotNav"
import Pathicon from "@pathwright/ux/src/components/pathicon/Pathicon"
import { ReactNode, useMemo } from "react"
import {
  CohortSyncPathCohortFragment,
  PathItemFragment,
  ResourceFragment
} from "@pathwright/graphql-client/src/generated"
import ResourceContext from "../../path/ResourceContext"
import { PREVIEW } from "../../path/constants"
import InlinePathNavigator from "../../path/navigator/InlinePathNavigator"
import PathNavigatorProvider from "../../path/navigator/PathNavigatorContext"
import { RESOURCE_TYPE_COLLECTION } from "../../resource/constants"
import { useCoverPageContext } from "../CoverPageProvider"
import { useCoverPageTranslate } from "../utils"
import { useCoverPagePreviewItems } from "./utils"

export const PreviewPathDotNav = () => {
  const { t } = useCoverPageTranslate()
  const { previewNavItems, immersiveRoute, getImmersiveRoutePath } =
    useCoverPageContext((v) => v)

  if (!previewNavItems?.length) return null

  const activeId =
    immersiveRoute?.name === "preview" && immersiveRoute.id != null
      ? immersiveRoute.id
      : "path"

  const activeIndex = previewNavItems.findIndex(({ source_id }) =>
    source_id === activeId ? true : Number(source_id) === Number(activeId)
  )
  const currentIndex = activeIndex >= 0 ? activeIndex : 0
  const prevItem = currentIndex > 0 ? previewNavItems[currentIndex - 1] : null
  const nextItem =
    currentIndex < previewNavItems.length - 1
      ? previewNavItems[currentIndex + 1]
      : null

  const prevTo = useMemo(
    () =>
      prevItem && getImmersiveRoutePath
        ? getImmersiveRoutePath({
            name: "preview",
            id: prevItem.source_id
          })
        : null,
    [prevItem, getImmersiveRoutePath]
  )

  const nextTo = useMemo(
    () =>
      nextItem && getImmersiveRoutePath
        ? getImmersiveRoutePath({
            name: "preview",
            id: nextItem.source_id
          })
        : null,
    [nextItem, getImmersiveRoutePath]
  )

  return (
    <PathPreviewNavigator
      centered
      renderTrigger={({ onOpen, onClose }) => (
        <Box
          as="span"
          maxHeight="100px"
          onMouseEnter={onOpen}
          onClick={onClose}
        >
          <DotNav
            items={previewNavItems}
            activeIndex={currentIndex}
            getItemKey={(item) => String(item.source_id)}
            prev={
              prevItem && prevTo
                ? {
                    to: prevTo
                  }
                : undefined
            }
            renderDot={({ item, index, isActive }) => {
              const isPathDot = item.source_id === "path"
              const dotClassName = [
                "DotNavBullet",
                isActive
                  ? "DotNavBullet--complete"
                  : "DotNavBullet--incomplete",
                isActive ? "DotNavBullet--active" : null
              ]
                .filter(Boolean)
                .join(" ")
              const dotTo = getImmersiveRoutePath?.({
                name: "preview",
                id: item.source_id
              })
              const ariaLabel = isPathDot
                ? t("immersive.nav.preview", { defaultValue: "Preview" })
                : `${t("path_preview.step", {
                    defaultValue: "Step"
                  })} ${index}`
              const dot = <Box as="span" className="DotNavBullet__dot" />
              if (!dotTo) {
                return (
                  <Box
                    as="span"
                    className={dotClassName}
                    aria-label={ariaLabel}
                  >
                    {dot}
                  </Box>
                )
              }
              return (
                <Link
                  to={dotTo}
                  className={`Link ${dotClassName}`.trim()}
                  aria-label={ariaLabel}
                  textDecoration="none"
                  onClick={(event) => event.stopPropagation()}
                >
                  {dot}
                </Link>
              )
            }}
            next={
              nextItem && nextTo
                ? {
                    to: nextTo
                  }
                : undefined
            }
          />
        </Box>
      )}
    />
  )
}

type PathPreviewNavigatorProps = {
  centered?: boolean
  startExpanded?: boolean
  renderTrigger?: (args: {
    onOpen: () => void
    onClose: () => void
  }) => ReactNode
}

const PathPreviewNavigator = ({
  centered = false,
  startExpanded = true,
  renderTrigger
}: PathPreviewNavigatorProps) => {
  const { t } = useCoverPageTranslate()
  const resource = useCoverPageContext((v) => v.resource)
  const getImmersiveRoutePath = useCoverPageContext(
    (v) => v.getImmersiveRoutePath
  )
  const { galleryItems, pathItems: lessons } = useCoverPagePreviewItems()
  const cohort = resource?.preview_group as CohortSyncPathCohortFragment | null
  const cohortId = cohort?.id ? Number(cohort.id) : null

  const lessonsWithPreviewableItems = lessons?.filter((lesson) =>
    lesson?.items?.some((item) => item?.is_previewable)
  )

  if (
    !cohort ||
    !cohortId ||
    !galleryItems?.length ||
    resource?.resource_type === RESOURCE_TYPE_COLLECTION
  )
    return (
      <Text asChild>
        <span>{t("immersive.nav.preview", { defaultValue: "Preview" })}</span>
      </Text>
    )

  return (
    <ScrollView>
      <HStack
        className="PathPreviewNavigator"
        align="center"
        gap={1}
        cursor="pointer"
        justify={centered ? "center" : undefined}
        w={centered ? "100%" : undefined}
      >
        <ResourceContext.Provider
          value={{
            // Keep the navigator scoped to the same group/path used to render
            // the preview cards.
            pathId: null,
            userId: null,
            cohortId: cohortId,
            resource: resource as ResourceFragment,
            group: cohort,
            registration: {
              id: 12345
            }
          }}
        >
          <PathNavigatorProvider
            cohort={cohort}
            resource={resource as ResourceFragment}
            pathMode={PREVIEW}
            displayFilterMenu={false}
            getResultURL={(item: PathItemFragment, blockSuffix?: string) => {
              if (item.type !== "step") {
                return null
              }
              const stepRoute = getImmersiveRoutePath?.({
                name: "preview",
                id: Number(item.source_id)
              })
              if (stepRoute && blockSuffix) {
                return `${stepRoute}#${blockSuffix}`
              }
              return stepRoute ?? null
            }}
            getResultProps={(item: PathItemFragment) => {
              if (item.type !== "step" && item.type !== "lesson") {
                return {}
              }

              const isLessonWithPreviewableItems =
                lessonsWithPreviewableItems?.some(
                  (lesson) => lesson.source_id === item.source_id
                )

              const itemIsPreviewable = item.is_previewable

              return itemIsPreviewable || isLessonWithPreviewableItems
                ? {
                    textDecoration:
                      item.type === "lesson" ? "none" : "underline",
                    textDecorationColor: "whiteAlpha.700",
                    textUnderlineOffset: "2px",
                    textDecorationThickness: "1px",
                    color: "whiteAlpha.900",
                    fontWeight: "700",
                    css: {
                      "& p": {
                        color: "whiteAlpha.900"
                      }
                    }
                  }
                : {
                    textDecoration: "none",
                    color:
                      item.type === "lesson"
                        ? "whiteAlpha.600"
                        : "whiteAlpha.200",
                    fontWeight: "300",
                    pointerEvents: "none",
                    cursor: "default",
                    tabIndex: -1,
                    "aria-disabled": true,
                    css: {
                      "& p": {
                        color: "whiteAlpha.500"
                      }
                    }
                  }
            }}
          >
            <InlinePathNavigator
              interactive={!renderTrigger}
              startExpanded={startExpanded}
              displayMainNavigatorButton={false}
              renderTrigger={({ onOpen, onClose }) =>
                renderTrigger ? (
                  renderTrigger({ onOpen, onClose })
                ) : (
                  <HStack align="center" gap={1}>
                    <Heading
                      as="span"
                      // @ts-expect-error: TODO: get typing correct using chakra typegen https://chakra-ui.com/docs/get-started/cli#chakra-typegen
                      size="h7"
                      m={0}
                      color="PRIMARY_GRAY"
                      className="theme-heading theme-heading-semibold"
                    >
                      {t("immersive.nav.preview", { defaultValue: "Preview" })}
                    </Heading>
                    <Box
                      as="span"
                      bgColor="transparent"
                      color="PRIMARY_GRAY"
                      minW={0}
                      w="20px"
                      h="20px"
                      p={0}
                      aria-hidden="true"
                      children={<Pathicon icon="chevron-down" />}
                    />
                  </HStack>
                )
              }
            />
          </PathNavigatorProvider>
        </ResourceContext.Provider>
      </HStack>
    </ScrollView>
  )
}

export default PathPreviewNavigator
